import { isPlatformBrowser } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { UserService } from 'src/app/modules/account/shared/user.service';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { LoginPopupComponent } from '../../components/login-popup/login-popup.component';
import { betModels } from '../../models/betModels';
import { BetService } from '../../services/bet.service';
import { LocalStoreService } from '../../services/local-store.service';
import { SupabaseService } from '../../services/supabase.service';
import { SettingsService } from '../../services/settings.service';
import { setGeneralSettingsData } from 'src/app/store/general-settings/general-settings.actions';
import { Store } from '@ngrx/store';
import { setUserDetailsData } from 'src/app/store/user-details/user-details.actions';
import { selectUserDetailsData } from 'src/app/store/user-details/user-details.selectors';

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  providers: [DialogService],
})
export class MainLayoutComponent
  implements OnInit, OnDestroy, AfterContentChecked
{
  userBalance = 0;
  isHome = false;
  isBrowser = false;
  rightMenu;
  leftMenu;
  username = '';
  bettingSlipStatus = false;
  ref: DynamicDialogRef;
  betSlip$: BehaviorSubject<any> = new BehaviorSubject(null);
  info: any;
  raceDate: any;
  betStakes = betModels.stakes;
  clearAllSelectedRunners$ = new Subject();
  private _activeBetSlipIndex = 0;
  maintanceHide = false;
  get activeBetSlipIndex(): number {
    return this._activeBetSlipIndex;
  }
  set activeBetSlipIndex(value: number) {
    this._activeBetSlipIndex = value;
  }
  private _unsubscribeAll = new Subject();
  allBetsOnCart$ = this.betService.allAddedBetsInCart$.asObservable();

  constructor(
    public dialogService: DialogService,
    public authService: AuthService,
    private userService: UserService,
    private settingService: SettingsService,
    public router: Router,
    private store: Store,
    public route: ActivatedRoute,
    public betService: BetService,
    private cdref: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any,
    private localStoreService: LocalStoreService,
    private supabase: SupabaseService
  ) {
    this.getLocalDate.bind(this);
    this.router.events.subscribe((event) => {
      if (this.router.url === '/') {
        this.isHome = true;
      } else {
        this.isHome = false;
      }
      if (event instanceof NavigationEnd) {
        if (this.route.snapshot?.queryParamMap?.get('fbclid'))
          this.router.navigateByUrl('/');
      }
    });
  }
  maintanceSet() {
    this.localStoreService.setItem('maintanceHide', true);
    this.maintanceHide = true;
  }
  ngOnInit(): void {
    if (this.localStoreService.getItem('user')) {
      this.store.select(selectUserDetailsData).subscribe((user) => {
        if (user?.country) {
          this.authService.currentCountry$.next(user.country);
          this.authService.currentCurrency$.next(user.country.currency);
        } else {
          this.authService.getDefaultCountry();
          return;
        }
      });
    } else {
      this.authService.getDefaultCountry();
    }

    if (this.localStoreService.getItem('maintanceHide') !== true) {
      this.localStoreService.setItem('maintanceHide', false);
    }
    this.maintanceHide = this.localStoreService.getItem('maintanceHide');
    if (this.localStoreService.getItem('user')) {
      this.getUserAccount();
    }
    this.getSetting();

    this.router.events
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter(
          (event) =>
            event instanceof NavigationStart && !event.url.startsWith('/race/')
        ),
        tap(
          (event) => (
            (this.bettingSlipStatus = false),
            (this.betService.totalAllBetsOnCart = 0),
            this.betService.allAddedBetsInCart$.next([])
          )
        )
      )
      .subscribe();

    this.isBrowser = isPlatformBrowser(this.platformId);

    this.betSlip$
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter((info) => Boolean(info)),
        tap((info) => {
          this.bettingSlipStatus = info.bettingSlipStatus;

          let currentBetRanks: any = {
            [info.selectedBet?.type === 'ita'
              ? 2
              : info.selectedBet?.type === 'trita'
              ? 3
              : 1]: [],
          };

          if (
            [
              'exacta',
              'trifecta',
              'pmu_tierce',
              'pmu_quarte_plus',
              'pmu_quinte_plus',

              'trio',
            ].includes(info.selectedBet?.type)
          )
            currentBetRanks = { ...currentBetRanks, [2]: [] };
          if (['trita'].includes(info.selectedBet?.type))
            currentBetRanks = { ...currentBetRanks, [3]: [] };
          if (
            [
              'trifecta',
              'pmu_tierce',
              'pmu_quarte_plus',
              'pmu_quinte_plus',
            ].includes(info.selectedBet?.type)
          )
            currentBetRanks = { ...currentBetRanks, [3]: [] };
          if (
            ['pmu_quarte_plus', 'pmu_quinte_plus'].includes(
              info.selectedBet?.type
            )
          )
            currentBetRanks = { ...currentBetRanks, [4]: [] };

          if (
            [
              'quinella',
              'trifecta',
              'exacta',
              'pmu_tierce',
              'pmu_tierce_standard',
              'pmu_quarte_plus',
              'pmu_quinte_plus',
              'pmu_quinte_plus_standard',
              'pmu_quarte_plus_standard',
              'trio',
            ].includes(info.selectedBet?.type)
          )
            currentBetRanks = { ...currentBetRanks, [5]: [] };

          if (
            [
              'pmu_tierce_standard',
              'pmu_quarte_plus_standard',
              'pmu_quinte_plus_standard',
            ].includes(info.selectedBet?.type)
          )
            currentBetRanks = { [5]: [] };
          if (['pmu_quinte_plus'].includes(info.selectedBet?.type))
            currentBetRanks = { ...currentBetRanks, [6]: [] };
          this.info = this.bettingSlipStatus
            ? {
                ...info,
                selectedRunnersObjIterable: [],
                selectedRunnersObj: Boolean(info.selectedRunners?.length)
                  ? info.selectedRunners.reduce((prev, curr) => {
                      let runnerOrder =
                        curr.runnerOrder === 0 ? 1 : curr.runnerOrder;
                      if (
                        info.selectedBet?.type === 'quinella' &&
                        runnerOrder > 1
                      )
                        runnerOrder = 5;
                      if (info.selectedBet?.type === 'ita') runnerOrder = 2;
                      if (info.selectedBet?.type === 'trita') runnerOrder = 3;
                      if (
                        [
                          'pmu_quarte_plus_standard',
                          'pmu_tierce_standard',
                          'pmu_quinte_plus_standard',
                        ].includes(info.selectedBet?.type)
                      ) {
                        runnerOrder = 5;
                      }

                      if (!prev[runnerOrder]) {
                        return { ...prev, [runnerOrder]: [curr] };
                      } else {
                        return {
                          ...prev,
                          [runnerOrder]: [...prev[runnerOrder], curr],
                        };
                      }
                    }, currentBetRanks)
                  : currentBetRanks,
              }
            : null;
          if (Boolean(this.info?.selectedRunnersObj))
            this.info = {
              ...this.info,
              selectedRunnersObjIterable: Object.keys(
                this.info.selectedRunnersObj
              ).map((m) => [parseInt(m, 10), this.info.selectedRunnersObj[m]]),
            };
          if (Boolean(this.info?.details))
            this.raceDate = this.getLocalDate(this.info.details);
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    if (this.ref) {
      this.ref.close();
    }
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  showConfirmBetPopup(): void {
    this.authService
      .getCurrentUser()
      .pipe(take(1))
      .subscribe((currentUser) => {
        if (!currentUser) {
          this.ref = this.dialogService.open(LoginPopupComponent, {
            showHeader: true,
            dismissableMask: true,
            data: {
              reload: false,
            },
            closable: true,
            styleClass: 'login-modal',
            contentStyle: {
              'border-radius': '4px',
              padding: '0',
              'z-index': '999',
            },
          });
          this.ref.onClose.subscribe((loginSuccess?) => {
            if (loginSuccess) {
              this.betService.showConfirmBetPopupEvent$.next();
            }
          });
        } else {
          this.betService.showConfirmBetPopupEvent$.next();
        }
      });
  }

  getUserAccount(): void {
    this.userService.getUserAccount().subscribe((user) => {
      this.userBalance = user.balance;
      this.username = user.username;
      this.store.dispatch(setUserDetailsData({ data: user }));
    });
  }

  getLocalDate(race) {
    const startTimeUtc =
      race.StartTimeUtc !== null && race.StartTimeUtc !== undefined
        ? race.StartTimeUtc.replace(/-/g, '/')
        : undefined;
    const scheduleTimeUtc =
      race.ScheduleTimeUtc !== null && race.ScheduleTimeUtc !== undefined
        ? race.ScheduleTimeUtc.replace(/-/g, '/')
        : undefined;
    const localDate =
      startTimeUtc !== undefined
        ? new Date(startTimeUtc + ' UTC')
        : scheduleTimeUtc !== undefined
        ? new Date(scheduleTimeUtc + ' UTC')
        : null;
    return localDate !== null ? localDate : new Date();
  }

  getSetting() {
    this.settingService.getGeneralSettings().subscribe((data) => {
      this.store.dispatch(setGeneralSettingsData({ data }));
    });
  }

  clearAll(): void {
    this.clearAllSelectedRunners$.next();
  }

  logOut() {
    this.authService.logOut();
  }
}
